<template>
  <div class="image-uploader-container">
    <div class="content-upload-setImage">
      <!-- UploadCare (Uploader) -->
      <div class="uploader-content">
        <uploadcare
          :id="id"
          :context="context"
          :crop="imageProps.crop"
          :imagesOnly="imageProps.imagesOnly"
          :imageShrink="imageProps.imageShrink"
          :cdnBase="'https://img.binds.co'"
          :UPLOADCARE_LOCALE="'pt'"
          @success="onSuccess"
          @error="onError"
        >
          <div class="upload-wrapper">
            <div v-if="!success" class="upload-placeholder">
              <app-button
                class="btn-circle-accent"
                style="height: 40px; width: 40px;">
                <i class="material-icons">add_photo_alternate</i>
              </app-button>
            </div>
            <div v-else class="image-container-wrapper" @mouseenter="showMenu" @mouseleave="startHideCountdown">
              <div class="image-edit-overlay" v-if="!uploaderLoading" @click="handleMenuAction('insert-image')">
                <i class="material-symbols-outlined">edit</i>
              </div>
              <img class="img-container-box" :src="returnUrl()" alt="Uploaded image" />
              <div class="blur-effect"></div>
              <div class="loader-overlay" v-if="uploaderLoading">
                <div class="loader-spinner"></div>
              </div>
            </div>
          </div>
        </uploadcare>
      </div>

      <!-- Menu container with absolute positioning -->
      <div
        class="menu-container no-drag"
        v-if="menu.visible && !isDragging && menuOptions.length"
        :style="getMenuPosition"
        :class="{ 'force-hide': isDragging }"
        @mouseenter="cancelHideCountdown"
        @mouseleave="hideMenu">
        <CascadingMenu
          :menuId='id'
          :items="menuOptions"
          @submenu-action="handleMenuAction"
          @menu-hover="handleMenuHover"
        />
      </div>
    </div>
  </div>
</template>

<script>
import CascadingMenu from '@/components/cascadingMenu/CascadingMenu'
import { eventBus } from '@/_helpers/eventBus.js'

export default {
  name: 'ImageUploaderV2',
  components: {
    uploadcare: () => import('./UploadCareV2'),
    CascadingMenu,
    'app-button': () => import('@binds-tech/binds-design-system/src/components/Button/Button')
  },
  props: {
    title: String,
    icon: String,
    imageProps: Object,
    id: { type: String, required: true },
    imagesOnly: {
      type: Boolean,
      default: false
    },
    crop: {
      type: String,
      default: ''
    },
    imageShrink: {
      type: String,
      default: ''
    },
    context: {
      type: String,
      required: true
    },
    menuOptions: Array,
    disableMenus: { // New prop to disable menus during drag operations
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      menu: { visible: false, style: {} },
      showUploader: false,
      publicKey: '',
      success: false,
      signature: '',
      expire: 0,
      imageUrl: '',
      uuid: '',
      places: {
        startMessage: { commit: 'survey/updateMessageProps' },
        endMessages: { commit: 'survey/updateEndMessageProps' },
        answeredMessage: { commit: 'survey/updateImagesLogo' },
        emailSettings: { commit: 'sendhub/updateImageProps' },
        imageLogoUrl: { commit: 'account/updateImageLogoUrl' },
        imageMainLogo: { commit: 'account/updateImageMainLogo' },
        imageSmallLogo: { commit: 'account/updateImageSmallLogo' },
        surveySmallLogo: { commit: 'survey/updateImageSmallLogo' },
        userImage: { commit: 'account/updateUserImage' },
        matrixOptionImage: { commit: 'survey/updateImageOptionOnQuestion' },
        optionImageChoice: { commit: 'survey/updateImageOptionOnQuestion' },
        imageOnTitle: { commit: 'survey/updateImageOnTitle' }
      },
      uploaderLoading: false,
      hideCountdownTimer: null,
      hideDelay: 300, // ms delay before hiding menu
      isDragging: false,
      gallery: [],
      dialogRef: [],
      dialogSettings: {}
    }
  },
  created () {
    this.success = this.imageProps.success
    this.$root.$on('uploadcare-ready', this.handleUploaderReady)
    this.$root.$on('uploadcare-cancel', this.handleUploadCancel)
    eventBus.$on('menu-item-hover', this.handleMenuItemHover)

    // Listen for dragging events from the parent component
    eventBus.$on('dragging-started', this.handleDragStart)
    eventBus.$on('dragging-ended', this.handleDragEnd)
  },
  mounted () {
    if (this.imageProps.autoStartUpload) {
      this.success = true
      this.handleMenuAction('insert-image')
      this.$emit('resetAutoStartUpload')
    }
  },
  methods: {
    handleDragStart () {
      this.isDragging = true
      // Force close the menu before drag starts
      this.menu.visible = false
      // Force close any open dialogs
      if (this.dialogRef && this.dialogRef.reject) {
        this.dialogRef.reject()
      }
    },

    handleDragEnd () {
      this.isDragging = false
    },

    showMenu () {
      // Don't show menu if currently dragging or explicitly disabled via props
      if (this.isDragging || this.disableMenus || this.uploaderLoading) return

      clearTimeout(this.hideCountdownTimer)

      // Position and show the menu
      this.positionMenu()
      this.menu.visible = true
    },

    startHideCountdown () {
      // Set a delay to give time to reach the menu
      this.hideCountdownTimer = setTimeout(() => {
        this.hideMenu()
      }, this.hideDelay)
    },

    cancelHideCountdown () {
      clearTimeout(this.hideCountdownTimer)
    },

    hideMenu () {
      // Only hide if submenu isn't hovered
      if (!this.submenuHovered) {
        this.menu.visible = false
      }
    },

    handleMenuHover (isHovering) {
      this.submenuHovered = isHovering
      if (isHovering) {
        this.cancelHideCountdown()
      } else {
        this.hideMenu()
      }
    },

    handleMenuItemHover (status) {
      if (status.menuId === this.id) {
        this.submenuHovered = status.isHovering
      }
    },

    positionMenu () {
      // Remove this method as we're now using computed property
    },

    handleMenuAction (action) {
      switch (action) {
        case 'insert-image':
          this.uploaderLoading = true
          this.openUploader()
          break
        case 'delete-option':
          this.$emit('submenu-action', action)
          this.menu.visible = false
          break
        default:
          this.$emit('submenu-action', action)
      }
    },

    returnUrl () {
      if (this.imageProps?.value && typeof this.imageProps.value !== 'object') return this.imageProps.value
      if (this.imageProps?.logo) return this.imageProps.logo
      if (this.imageProps?.label) return this.imageProps.label
      return this.imageUrl
    },

    openUploader () {
      this.uploaderLoading = true
      this.$root.$emit('open-uploadcare', this.id)
    },

    onError (event) {
      this.uploaderLoading = false
      console.log('🦕 err', event, '💥')
    },

    onSuccess (event) {
      if (this.imageProps) {
        this.imageUrl = event.cdnUrl
        // this.$store.commit('sendhub/cropWidth', event.crop.width)
        // if (this.imageProps.where === 'emailSettings' || this.imageProps.where === 'imageLogoUrl') {
        //   this.imageUrl = `${event.cdnUrl}-/setfill/ffffff/-/format/jpeg/-/resize/260x/`
        // }
        this.imageProps.value = this.imageUrl
        this.imageProps.logo = this.imageUrl
        this.imageProps.imageUrl = this.imageUrl
        this.uuid = event.uuid
        this.imageProps.uuid = this.uuid
        this.success = true
        const objectToCommit = this.formatCommit()

        this.$store.commit(objectToCommit.commit, objectToCommit.object)
        this.$emit('doneWithSuccess')

        // Ensure loading is turned off when upload completes successfully
        this.uploaderLoading = false

        this.$forceUpdate()
      }
    },

    formatCommit () {
      let objectToCommit = {}
      Object
        .keys(this.places)
        .forEach(function (key, index) {
          if (key === this.imageProps.where) {
            objectToCommit = this.places[key]
            objectToCommit.object = { ...this.imageProps }
            objectToCommit.object.value = this.imageUrl
            objectToCommit.object.logo = this.imageUrl
            objectToCommit.object.alias = 'logo'
            objectToCommit.object.imageUrl = this.imageUrl
            objectToCommit.object.uuid = this.uuid
            objectToCommit.object.where = key

            // Special handling for imageOnTitle
            if (key === 'imageOnTitle') {
              objectToCommit.object.imageIndex = this.imageProps.imageIndex
            }
          }
        }, this)
      return objectToCommit
    },
    handleUploaderReady () {
      // This event is triggered when uploadcare widget is ready or closed
      this.uploaderLoading = false
    },
    handleUploadCancel () {
      // This event is triggered when the upload is canceled
      this.uploaderLoading = false
    }
  },
  beforeDestroy () {
    this.$root.$off('uploadcare-ready', this.handleUploaderReady)
    this.$root.$off('uploadcare-cancel', this.handleUploadCancel)
    eventBus.$off('menu-item-hover', this.handleMenuItemHover)
    eventBus.$off('dragging-started', this.handleDragStart)
    eventBus.$off('dragging-ended', this.handleDragEnd)
    clearTimeout(this.hideCountdownTimer)
  },
  computed: {
    getMenuPosition () {
      if (!this.menu.visible) return {}

      const element = this.$el.querySelector('.img-container-box')
      if (!element) return this.menu.style

      const rect = element.getBoundingClientRect()
      return {
        position: 'absolute',
        top: `${element.offsetHeight + 5}px`,
        left: '0',
        zIndex: 1000
      }
    }
  }
}
</script>

<style scoped lang="scss">
.image-uploader-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: block;
}

.content-upload-setImage {
  position: relative;
  width: 100%;
  height: 100%;
  display: block;
  margin: 0;
}

.uploader-content {
  position: relative;
  width: 100%;
  height: 100%;
  display: block;
}

.upload-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #f8f8f8;
  border-radius: 4px;
  border: 1px dashed #cecece;
  overflow: visible;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--accent-color, #ff607d);
    background-color: #f0f0f0;
  }
}

.upload-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  z-index: 1;
}

.btn-circle-accent {
  width: 48px !important;
  height: 48px !important;
  min-width: 48px !important;
  min-height: 48px !important;
  padding: 0 !important;
  background-color: var(--accent-color, #ff607d) !important;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  border: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: scale(1.1);
  }

  i {
    font-size: 24px !important;
    color: white !important;
  }
}

.image-container-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  border-radius: 4px;
  display: block;

  &:hover .image-edit-overlay {
      opacity: 1;
    }

    .image-edit-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.2s ease;
      cursor: pointer;
      z-index: 2;
      border-radius: 4px;

      i {
        color: white;
        font-size: 32px;
      }
    }
}

.img-container-box {
  width: 100% !important;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
  transition: all 0.3s ease;
  display: block;
  background-color: #f8f8f8;
  padding: 5px;
  border-radius: 10px;
}

.submenu-uploader {
  position: absolute;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  padding: 5px;
  width: 150px;
  z-index: 1000;
}

.submenu-uploader button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  padding: 10px;
  width: 100%;
  text-align: left;
  cursor: pointer;
}

.submenu-uploader button:hover {
  background: #f5f5f5;
}

.material-icons-outlined {
  font-size: 20px;
}

.blur-effect {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  background-color: rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  pointer-events: none;
  border-radius: 4px;
  backdrop-filter: blur(0px);
}

.image-container-wrapper:hover .img-container-box {
  border-color: var(--accent-color, #ff607d) !important;
}

.image-container-wrapper:hover .blur-effect {
  opacity: 1;
  backdrop-filter: blur(1px);
}

.loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  z-index: 10;
}

.loader-spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid var(--accent-color, #ff607d);
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.image-uploader-container {
  position: relative;
  display: inline-block;
}

.menu-container {
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  margin-top: 5px;
  z-index: 1000;
  pointer-events: auto;
  transition: opacity 0.2s ease;
}

/* Prevent the menu from being included in drag operations */
.no-drag {
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  user-drag: none;
  pointer-events: auto;
}

.uploader-content {
  position: relative;
  z-index: 1;
}

/* Add styles to prevent menu from being dragged */
.menu-container * {
  pointer-events: auto;
  user-select: none;
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
}

.force-hide {
  display: none !important;
  visibility: hidden !important;
  opacity: 0 !important;
  pointer-events: none !important;
}

.dragging .menu-container,
.ghost-card .menu-container {
  display: none !important;
  visibility: hidden !important;
  opacity: 0 !important;
}
</style>
